import { Chip, Stack, Typography } from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { WorkQueueColumnKey, WorkQueueFilterKey } from "encamp-shared/src/work/filterKeys";
import { ActivityStatus, WorkflowStatus } from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { DRAWER_PARAM, toDrawerParam } from "routes/Staff/GeneratorStatusChanges/drawerDeepLink";
import { MANIFEST_REVIEW_MANIFEST_PARAM } from "routes/Staff/WasteFulfillment/ManifestReview/manifestDeepLink";

import { UnifiedWorkRow } from "./types";

/**
 * The nine seeded WorkType keys, each mapped to the verb the detail drawer's
 * primary action uses — "what happens on the other page", not "open row". The
 * grid's work-type filter enumerates the keys of this record, so the facet and
 * the action labels can't drift apart.
 *
 * First-wave rows resolve to TIER2_REPORT/SUBMISSION/COHORT/CUSTOM via the
 * legacy Workflow.type fallback; the rest arrive with the stub-workflow second
 * wave.
 */
const WORK_TYPE_ACTION_LABELS: Record<string, string> = {
  MANIFEST: "Review manifest",
  GENERATOR_PERIOD: "Review generator status change",
  CREDENTIAL: "Open credential",
  ECHO_VIOLATION: "Review violation",
  TIER2_REPORT: "Open Tier II report",
  SUBMISSION: "Open submission",
  COHORT: "Open cohort filing",
  CUSTOM: "Open work steps",
};

const WORK_TYPE_KEYS = Object.keys(WORK_TYPE_ACTION_LABELS);

/**
 * Label for the drawer's primary action. Deliberately says what the linked
 * surface is for; the destination itself comes from {@link getRowLink}.
 */
export function getRowActionLabel(row: UnifiedWorkRow): string {
  const label = row.workTypeKey ? WORK_TYPE_ACTION_LABELS[row.workTypeKey] : undefined;
  return label ?? "Open work item";
}

/**
 * Deep-link each row to its existing per-type surface. At most one subject FK
 * is set per workflow (workflow_one_subject CHECK), so the chain below is a
 * dispatch, not a priority order.
 */
export function getRowLink(row: UnifiedWorkRow): string | null {
  // Custom deliverables own the one new detail surface: the step-list screen.
  if (row.workTypeKey === "CUSTOM") {
    return `/staff/work/${row.workflowId}`;
  }
  if (row.tierIIReportId) {
    if (row.activityId) {
      return `/staff/fulfillment/r/${row.tierIIReportId}/activities/${row.activityId}`;
    }
    return `/staff/fulfillment/r/${row.tierIIReportId}`;
  }
  if (row.cohortId) {
    return `/staff/cohorts/${row.cohortId}`;
  }
  if (row.submissionId) {
    return `/staff/fulfillment/waste/submissions/${row.submissionId}`;
  }
  // Generator exceedance: the dashboard's action drawer deep-links by the
  // exceedance's natural key and self-hydrates even when the row sits outside
  // the grid's default filters.
  if (row.exceedanceFacilityId && row.exceedanceSource && row.exceedancePeriodStart) {
    const drawer = toDrawerParam({
      facilityId: row.exceedanceFacilityId,
      source: row.exceedanceSource,
      periodStart: row.exceedancePeriodStart,
    });
    return `/staff/generator-status-changes?${DRAWER_PARAM}=${encodeURIComponent(drawer)}`;
  }
  // Manifest review: filter the queue to this manifest and auto-open its
  // detail dialog (the `manifest` param is one-shot, consumed by the page).
  if (row.processedManifestId) {
    const params = new URLSearchParams();
    if (row.manifestTrackingNumber) {
      params.set("omnisearch", `manifestNumber:${row.manifestTrackingNumber}`);
    }
    params.set(MANIFEST_REVIEW_MANIFEST_PARAM, row.processedManifestId);
    return `/staff/fulfillment/waste/manifest-review?${params.toString()}`;
  }
  if (row.credentialImplementationId) {
    return `/staff/credentials/epcra/${row.credentialImplementationId}`;
  }
  // Violations live on the customer insights surface (staff can browse it);
  // filter it to this violation's EPA natural key.
  if (row.harmonizedViolationId && row.tenantId) {
    const search = row.harmonizedViolationKey
      ? `?omnisearch=${encodeURIComponent(`violationId:${row.harmonizedViolationKey}`)}`
      : "";
    return `/o/${encodeURIComponent(row.tenantId)}/insights/violations${search}`;
  }
  return null;
}

/**
 * Filter-dialog-only keys. "tenant" renders the MultipleTenantPicker
 * (search-as-you-type suggested tenants) and serializes to `tenantId:<ids>`,
 * which the unifiedWorkItems resolver filters with an IN.
 */
export const ADDITIONAL_FILTER_KEYS: (FilterKey & { key: WorkQueueFilterKey })[] = [
  { key: "tenant", header: "Tenant", filterKeyType: "multipleTenant" },
];

export function formatEstimate(minutes: number | null | undefined): string {
  // Zero is a deliberate estimate (the resolver accepts it), distinct from unset.
  if (minutes == null) {
    return "—";
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

/**
 * Typed so a filterable column cannot exist without the API knowing its key.
 * `OmnisearchDataGridV2` turns every column's `field` into a filter key, and a
 * key the resolver does not register is dropped in silence — the grid shows
 * unfiltered rows while the search box claims a filter is active. Narrowing
 * `field` here makes that a compile error instead of a bug report.
 */
type WorkQueueColDef = OmnisearchGridColDef<UnifiedWorkRow> & { field: WorkQueueColumnKey };

export const COLUMNS: WorkQueueColDef[] = [
  {
    field: "workType",
    headerName: "Work type",
    width: 190,
    filterKeyType: "enum",
    enumValues: WORK_TYPE_KEYS,
    valueGetter: (_value, row) => row.workTypeLabel ?? prettyPrintEnumValue(row.workTypeKey),
  },
  {
    field: "title",
    headerName: "Work item",
    flex: 1.4,
    minWidth: 220,
    filterKeyType: "text",
    renderCell: ({ row }) => (
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {row.title ?? "—"}
        </Typography>
        {(row.activityDescription ?? row.activityType) && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.activityDescription ?? prettyPrintEnumValue(row.activityType)}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    // Filtering happens through the "tenant" picker key below (suggested
    // tenants, filters by id); this column only displays and sorts the name.
    field: "tenantName",
    headerName: "Tenant",
    flex: 1,
    minWidth: 160,
  },
  {
    field: "facilityName",
    headerName: "Facility",
    flex: 1,
    minWidth: 150,
    filterKeyType: "text",
    valueGetter: (_value, row) => row.facilityName ?? "—",
  },
  {
    field: "state",
    headerName: "State",
    width: 90,
    filterKeyType: "text",
  },
  {
    field: "workflowStatus",
    headerName: "Status",
    width: 130,
    filterKeyType: "enum",
    enumValues: Object.values(WorkflowStatus),
    valueGetter: (_value, row) => prettyPrintEnumValue(row.workflowStatus),
  },
  {
    field: "activityStatus",
    headerName: "Step status",
    width: 130,
    filterKeyType: "enum",
    enumValues: Object.values(ActivityStatus),
    valueGetter: (_value, row) =>
      row.activityStatus ? prettyPrintEnumValue(row.activityStatus) : "—",
  },
  {
    // Filters through the `tag` key (array-overlap on the workflow's tag
    // names; `tag:null` for untagged); display-only otherwise, so no sort.
    field: "tag",
    headerName: "Tags",
    width: 160,
    sortable: false,
    filterKeyType: "text",
    nullCheckboxLabel: "Untagged only",
    renderCell: ({ row }) =>
      row.tags.length === 0 ? (
        <Typography variant="body2">—</Typography>
      ) : (
        <Stack direction="row" spacing={0.5} sx={{ overflow: "hidden", alignItems: "center" }}>
          {row.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" />
          ))}
        </Stack>
      ),
  },
  {
    field: "assigneeEmail",
    headerName: "Assignee",
    flex: 1,
    minWidth: 170,
    // The staff-user picker (same as Generator Status Changes / CERS), which
    // serializes to exact emails — the resolver matches them with IN, not a
    // substring. Unassigned-only lives on the stat card (`assigneeEmail:null`),
    // which the resolver still honors.
    filterKeyType: "multipleStaffAnyUser",
    valueGetter: (_value, row) => row.assigneeEmail ?? "—",
  },
  {
    field: "dueAt",
    headerName: "Due",
    width: 130,
    renderCell: ({ row }) => {
      if (!row.dueAt) {
        return <Typography variant="body2">—</Typography>;
      }
      const due = DateTime.fromISO(row.dueAt);
      const overdue = due < DateTime.now();
      return (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" color={overdue ? "error" : undefined}>
            {due.toLocaleString(DateTime.DATE_MED)}
          </Typography>
          {row.dueType && (
            <Typography variant="caption" color="text.secondary">
              {prettyPrintEnumValue(row.dueType)}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    field: "estimateMinutes",
    headerName: "Est.",
    width: 80,
    valueGetter: (_value, row) => formatEstimate(row.estimateMinutes),
  },
];
